import { CloudDownload, Inbox, Loader, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import Badge from "../UI/Badge";
import { can_visit, countSegments, usPhoneFormat } from "../../helpers";
import Spacing from "../Spacing";
import { notify } from "../../helpers/index";
import { getSMSLimitData, submitBulkMessage } from "../../services/message";
import { Config } from "../../Config";
import { Drawer } from 'vaul';
import Select from "react-select";
import Papa from 'papaparse';

// US Phone Number Validator & Formatter
function usPhoneNumber(phone) {
  if (typeof phone !== 'string') return null;

  const cleaned = phone.replace(/[^0-9+]+/g, '');
  let digits = cleaned;

  if (digits.startsWith('+1')) {
    digits = digits.slice(2);
  }

  if (digits.length !== 10) return null;

  const formatted = `+1${digits}`;
  const validPattern = /^\+1[2-9]\d{2}[2-9]\d{2}\d{4}$/;

  return validPattern.test(formatted) ? formatted : null;
}

export default function MessageSendModal(props) {
  const { 
    messageTemplate = [], 
    account_id, 
    location_id, 
    smsLimit = 0, 
    setSmsLimit = () => {},
    reloadFilter = {},
    reloadData = () => {},
  } = props;


  const [uploadFile, setUploadFile] = useState(null);
  const [fileError, setFileError] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(true);
  const [activeModal, setActiveModal] = useState(false);
  const [selectedDataCount, setSelectedDataCount] = useState(0);
  const [segments, setSegments] = useState(0);
  const [requiredSms, setRequiredSms] = useState(0);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [messageBody, setMessageBody] = useState(null);
  const [chooseTemplate, setChooseTemplate] = useState(null);
  const [validList, setValidList] = useState([]);
  const [invalidList, setInvalidList] = useState([]);
  const [showInvalidPreview, setShowInvalidPreview] = useState(false);
  const invalidPreviewRef = useRef(null);
  const dropdownRef = useRef(null);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  useEffect(() => {
    if (messageBody && validList.length > 0) {
      const totalSegment = countSegments(messageBody);
      setSegments(totalSegment);
      setRequiredSms(totalSegment * validList.length);
    }
  }, [messageBody, validList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        invalidPreviewRef.current &&
        !invalidPreviewRef.current.contains(event.target)
      ) {
        setShowInvalidPreview(false);
      }
    };

    if (showInvalidPreview) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInvalidPreview]);
  
  const handleFileChange = async(e) => {
    const selectedFile = e.target.files[0];
    const isCSV = selectedFile?.type === "text/csv" || selectedFile?.name?.toLowerCase()?.endsWith(".csv");

    if (selectedFile && !isCSV) {
      setFileError(true);
      setUploadDisabled(true);
      setUploadFile(null);
    } else {
      setFileError(false);
      setUploadDisabled(false);
      setUploadFile(selectedFile);

      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data, errors }) => {
          const valid = [];
          const invalid = [];

          if (errors.length > 0) {
            console.error('CSV Parsing Errors:', errors);
            return;
          }

          for (const row of data) {
            const first = row?.first_name?.trim() || '';
            const last = row?.last_name?.trim() || '';
            const rawPhone = row?.phone_number?.trim() || '';
            const formatted = usPhoneNumber(rawPhone);

            const entry = {
              first_name: first,
              last_name: last,
              phone_number: rawPhone,
            };

            if (formatted) {
              valid.push({ ...entry, phone_number: formatted });
            } else {
              invalid.push(entry);
            }
          }
          
          setValidList(valid);
          setInvalidList(invalid);
          setSelectedDataCount(valid.length);
        },
        error: (err) => {
          console.error('Failed to parse CSV file:', err);
        },
      });
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setMessageBody(value);
    var totalSegment = countSegments(value);
    setSegments(totalSegment);
    setRequiredSms(totalSegment*selectedDataCount);
  };

  const copyText = (value) => {
    navigator.clipboard.writeText(value);
    // notify("success", "Copied "+value, 0.4)

    const messageTextarea = document.querySelector(
      'textarea[name="message_body"]'
    );
    const startPaste = messageTextarea.selectionStart;
    const endPaste = messageTextarea.selectionEnd;

    const newText = messageTextarea.value.substring(0, startPaste) + value + messageTextarea.value.substring(endPaste, messageTextarea.value.length);

    setMessageBody(newText);
    setSegments(countSegments(newText));
    setRequiredSms(countSegments(newText)*selectedDataCount);
  };

  const allMessageTemplate = messageTemplate?.map((option) => ({
    value: option?.id,
    label: option?.title,
  }));

  const handleTemplateChange = (selectedOption) => {
    const selectedTemplateId = selectedOption.value;
    setChooseTemplate(selectedTemplateId);
    const selectedTemplate = messageTemplate.find(
      (template) => template.id === parseInt(selectedTemplateId)
    );
    if (selectedTemplate) {
      setMessageBody(selectedTemplate.body);
      var totalSegment = countSegments(selectedTemplate.body);
      setSegments(totalSegment);
      setRequiredSms(totalSegment*selectedDataCount);
    }
  };

  const getSMSLimit = async() => {
    const result = await getSMSLimitData(account_id);
    if (result) {
      setSmsLimit(result?.data);
    }
  }

  const handleFileSubmit = async () => {
    if (!uploadFile) {
      notify("error", "Please select a file");
      return;
    }

    if (!messageBody) {
      notify("error", "Please select Message body");
      return;
    }

    if(can_visit("_bulk_message")){
      setSubmitLoader(true);
      const messageTransactions = validList.map((patient) => ({
        notification_type: "Bulk",
        patient_id: null,
        message_body: messageBody
          .replace(/\[first_name\]/gi, patient.first_name || "")
          .replace(/\[last_name\]/gi, patient.last_name || ""),
        receiver_address: patient.phone_number,
      }));

      try {
        const response = await submitBulkMessage(
          { message_transactions: messageTransactions },
          account_id,
          location_id
        );

        if (response?.detail) {
          notify("error", response.detail);
        } else if (response?.data?.status === "success") {
          notify("success", messageTransactions.length + " " + response?.data?.message);
          reloadData(reloadFilter);
          handleDrawerClose();
          setTimeout(() => {
            getSMSLimit();
          }, 5000);
        } else {
          notify("error", response?.data?.message || "Something went wrong");
        }
      } catch (error) {
        notify("error", error?.response?.data?.detail || "Unexpected error occurred");
      } finally {
        setSubmitLoader(false);
      }
    }
  };

  const sendBulkSms = () => {
    setActiveModal(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };

  const handleDrawerClose = () => {
    setFileError(false);
    setSegments(0);
    setSelectedDataCount(0);
    setUploadFile(null);
    setMessageBody(null);
    setChooseTemplate(null);
    setValidList([]);
    setInvalidList([]);
    setShowInvalidPreview(false);
    setUploadDisabled(true);
    setSubmitLoader(false);
    setRequiredSms(0)
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={setActiveModal} onClose={handleDrawerClose}>
        <Drawer.Trigger 
          className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn"
          style={{ padding: '7px 12px' }}
          onClick={() => sendBulkSms()}
        >
          <Inbox width={20} height={20} />
          <span>Send Bulk Message</span>
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4 large">
            <Drawer.Title className="sr-only">Bulk Message</Drawer.Title>
            <Drawer.Description className="sr-only">Send bulk message</Drawer.Description>
            <div className="docpro_modal_wrap docpro_modal_lg center">
              <div className="docpro_modal" style={{ backgroundColor: "#F9F9FB" }}>
                <button className="docpro_close_btn" onClick={() => {handleDrawerClose();}}>
                  <X />
                </button>

                <h2 className="docpro_fs_24 docpro_mb_0">Send Bulk Messages</h2>
                <Spacing lg="20" sm="20" />
                
                <label className="docpro_primary_color rejection_sheet">
                  <span>
                    <span className="docpro_fw_500">Import File</span> <small className="docpro_red_color">(.csv)</small>
                  </span>
                  <a 
                    href={`${Config.appUrl}/pdf/bulk_message_patient_list.csv`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download="Bulk-Message-Patient-List.csv" 
                    title="click here to download Sample" 
                    className="docpro_red_color docpro_text_btn_2"
                    style={{ marginBottom: 0 }}
                  ><CloudDownload /> Download Sample</a>
                </label>
                <div className='input_file_conatiner'>
                  <label htmlFor="file-upload" className='file_button'>
                    Choose Files
                  </label>
                  <input
                    type="file"
                    id="file-upload"
                    accept=".csv"
                    className='file_hidden_input'
                    onChange={handleFileChange}
                  />
                  <span className='file_label'>{uploadFile ? `${uploadFile?.name}` : 'No file chosen'}</span>
                </div>
                {fileError && (
                  <p className="docpro_error_message mb-0" style={{ marginTop: 0 }}>
                    Please upload a valid csv file.
                  </p>
                )}

                <Spacing lg="20" sm="20" />
                <label className="docpro_fw_500 docpro_primary_color">
                  Message Template
                </label>
                <Select
                  options={allMessageTemplate}
                  placeholder="Search Template..."
                  isSearchable
                  value={allMessageTemplate?.find((option) => option?.value === chooseTemplate) || null}
                  onChange={handleTemplateChange}
                  className="react-select-container w-full"
                  classNamePrefix="react-select"
                  filterOption={(option, rawInput) => {
                    const words = rawInput.toLowerCase().split(" ");
                    return words.every((word) =>
                      option.label.toLowerCase().includes(word)
                    );
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '40px',
                      borderColor: 'var(--border)',
                      borderRadius: '8px'
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 1050,
                      marginTop: '-2px',
                      borderRadius: '0 0 3px 3px',
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 1050,
                      padding: 0
                    }),
                  }}
                />
                <Spacing lg="20" sm="20" />
                <label className="docpro_fw_500 docpro_primary_color">
                  Message Body <span className="docpro_red_color">*</span>{" "}
                  <span
                    className="docpro_badge docpro_badge_purple "
                    onClick={() => copyText("[first_name] ")}
                  >
                    [first_name]{" "}
                  </span>{" "}
                  <span
                    className="docpro_badge docpro_badge_purple "
                    onClick={() => copyText("[last_name] ")}
                  >
                    [last_name]{" "}
                  </span>
                </label>
                <textarea
                  required
                  minLength={2}
                  name="message_body"
                  className="docpro_form_field size_lg"
                  cols="30"
                  rows="8"
                  value={messageBody}
                  onChange={handleChange}
                  placeholder="Write your message here..."
                ></textarea>
                <Spacing lg="5" sm="5" />
                <span className="docpro_primary_color docpro_fw_500">
                  Total Segment{" "}
                  <Badge color="cyan" square>
                    {segments || 0}
                  </Badge>
                </span>

                <Spacing lg="12" sm="12" />
                <hr />
                <Spacing lg="15" sm="15" />
                <div className="bulk_message_footer docpro_grid_gap_10 docpro_bulk_sms_button_sections">
                  <div>
                    <p className="docpro_mb_0">Available Message Credit: {smsLimit.available_credit}</p>
                    <p className="docpro_mb_0 flex items-center">
                      <span className="mr-2">
                        Required Message Credit: {requiredSms} 
                      </span>
                      {requiredSms > (smsLimit.available_credit) ? 
                        <button className="docpro_icon_btn overview" style={{ width: 'auto', height: 'auto', borderRadius: '50%' }} >
                          <span className="docpro_red_color">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-alert"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
                          </span>
                          <div className="docpro_tip">
                            <span>Insufficient credits</span>
                          </div>
                        </button>
                      : 
                        <span className="docpro_green_color">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield-check"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
                        </span>
                      }
                    </p>
                  </div>
                  <div className='flex items-center docpro_grid_gap_10 small_buttons docpro_mt_auto docpro_bulk_sms_buttons'>
                    {invalidList.length > 0 && (
                      <div className="relative">
                        <button
                          type='button'
                          className="docpro_bordered_btn docpro_fw_600 size_lg docpro_red_color docpro_view_phone_number"
                          style={{ borderColor: '#e5484d'}}
                          onClick={() => setShowInvalidPreview((prev) => !prev)}
                        >
                          View Invalid Numbers
                        </button>
                        {showInvalidPreview && (
                          <div ref={invalidPreviewRef} className="docpro_invalid_number_list text-sm">
                            <h6 className="font-semibold mb-2" style={{ paddingLeft: '3px' }}>Invalid Phone Numbers</h6>
                            <ul className="docpro_custom_list custom-scroll">
                              {invalidList.length > 0 ? (
                                invalidList.map((msg, idx) => (
                                  <li key={idx}>
                                    {msg?.last_name}{" "}{msg?.first_name}:{" "}
                                    <span className="docpro_red_color">{msg?.phone_number ? usPhoneFormat(msg?.phone_number) : "N/A"}</span>
                                  </li>
                                ))
                              ) : (
                                <li>No invalid messages found.</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    <button
                      className="docpro_bordered_btn size_lg docpro_fw_600 docpro_white_color docpro_filed_btn"
                      onClick={handleFileSubmit}
                      disabled={uploadDisabled ? true : false}
                    >
                      <span>Submit Now</span>
                    </button>
                  </div>
                </div>
            
                {submitLoader && (
                  <span className="docpro_loading" style={{ backgroundColor: 'rgb(218 218 218 / 61%)' }}>
                    <Loader width={35} height={35} className="docpro_accent_color" />
                  </span>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
